import {
  splitDeviceChannelRoleSkeleton
} from './deviceChannelMenuSplit'
import { reparentLeoOpsOutOfSystemSettings } from './leoSystemSettingsMenuAlign'

/** nav 树 → 扁平（保留 type；侧栏与角色树对齐用） */
export function flattenNavTreeToFlatList (nodes, parentId = '0', acc = []) {
  if (!Array.isArray(nodes)) return acc
  nodes.forEach((n) => {
    if (!n || n.id == null) return
    const id = String(n.id)
    const type = Number(n.type != null ? n.type : 1)
    const resolvedParent =
      n.parentId != null && n.parentId !== '' ? String(n.parentId) : String(parentId)
    acc.push({
      ...n,
      id,
      parentId: resolvedParent,
      type
    })
    if (type !== MENU_BUTTON && Array.isArray(n.menuList) && n.menuList.length) {
      flattenNavTreeToFlatList(n.menuList, id, acc)
    }
  })
  return acc
}

function navSkeletonToSidebarNodes (nodes) {
  if (!Array.isArray(nodes)) return []
  return nodes.map((n) => {
    const menuList = navSkeletonToSidebarNodes(n.menuList || [])
    return Object.assign({}, n, { menuList })
  })
}

/**
 * 侧栏导航骨架 = 角色授权树同款 parentId（含通道/设备配置 split）。
 * @param {Array} flatList 来自 nav 扁平或 select-for-role
 */
export function buildSidebarNavTreeFromFlat (flatList) {
  const flat = normalizeFlatMenus(flatList)
  const skeleton = splitDeviceChannelRoleSkeleton(buildNavSkeletonFromFlat(flat))
  return navSkeletonToSidebarNodes(skeleton)
}

function indexNavTreeById (nodes, map = new Map()) {
  if (!Array.isArray(nodes)) return map
  nodes.forEach((n) => {
    if (!n || n.id == null) return
    map.set(String(n.id), n)
    if (Array.isArray(n.menuList)) indexNavTreeById(n.menuList, map)
  })
  return map
}

/** 从原始 nav 树补全 name/icon/url（重建骨架后） */
export function enrichSidebarNodesFromNavTree (target, source) {
  const byId = indexNavTreeById(source)
  function walk (nodes) {
    if (!Array.isArray(nodes)) return
    nodes.forEach((n) => {
      if (!n || n.id == null) return
      const src = byId.get(String(n.id))
      if (src) {
        if (src.name != null) n.name = src.name
        if (src.enName != null) n.enName = src.enName
        if (src.icon != null) n.icon = src.icon
        if (src.url != null) n.url = src.url
        if (src.sort != null) n.sort = src.sort
        if (src.type != null) n.type = src.type
      }
      walk(n.menuList)
    })
  }
  walk(target)
}

const MENU_CATALOG = 0
const MENU_BUTTON = 2

function translateMenuKey (vm, key) {
  if (!vm || !key || !vm.$te || !vm.$te(key)) return null
  const text = vm.$t(key)
  if (!text || text === key) return null
  return text
}

/**
 * 與側欄 menuDisplayTitle 一致：優先 i18n(route.enName)，缺鍵時用 DB name。
 */
export function menuDisplayTitle (vm, m) {
  if (!m) return ''
  const en = m.enName
  if (en) {
    const fromRoute = translateMenuKey(vm, 'route.' + en)
    if (fromRoute) return fromRoute
    const fromTable = translateMenuKey(vm, 'table.' + en)
    if (fromTable) return fromTable
  }
  return m.name || en || ''
}

/** 按鈕／功能權限（type=2）操作名 */
export function buttonActionLabel (vm, m) {
  if (!m) return ''
  const en = m.enName
  if (en) {
    const routeKey = 'route.' + en
    if (vm.$te && vm.$te(routeKey)) {
      const t = vm.$t(routeKey)
      if (t && t !== routeKey) return t
    }
    const tableKey = 'table.' + en
    if (vm.$te && vm.$te(tableKey)) {
      const t = vm.$t(tableKey)
      if (t && t !== tableKey) return t
    }
  }
  if (m.perms) {
    const suffix = String(m.perms).split(':').pop()
    const fromPerm = 'table.' + suffix
    if (vm.$te && vm.$te(fromPerm)) return vm.$t(fromPerm)
  }
  return m.name || en || m.perms || ''
}

/**
 * 角色授權樹節點標籤：目錄／菜單同側欄；按鈕為「父菜單 · 操作」。
 */
export function rolePermissionNodeLabel (vm, m, parentMenu) {
  if (!m) return ''
  if (Number(m.type) === MENU_BUTTON && parentMenu) {
    return menuDisplayTitle(vm, parentMenu) + ' · ' + buttonActionLabel(vm, m)
  }
  return menuDisplayTitle(vm, m)
}

function sortMenuNodes (nodes) {
  if (!Array.isArray(nodes)) return nodes
  nodes.sort((a, b) => {
    const sa = Number(a.sort)
    const sb = Number(b.sort)
    const na = Number.isFinite(sa) ? sa : 0
    const nb = Number.isFinite(sb) ? sb : 0
    if (na !== nb) return na - nb
    return String(a.id).localeCompare(String(b.id), undefined, { numeric: true })
  })
  return nodes
}

function normalizeFlatMenus (flatList) {
  return (flatList || [])
    .filter((m) => m && String(m.id) !== '0' && Number(m.parentId) !== -1)
    .map((m) => ({
      ...m,
      id: String(m.id),
      parentId: m.parentId != null && m.parentId !== '' ? String(m.parentId) : '0',
      type: Number(m.type)
    }))
}

function indexFlatMenus (flat) {
  const byId = new Map()
  const buttonsByParent = new Map()
  flat.forEach((m) => {
    byId.set(String(m.id), m)
    if (m.type === MENU_BUTTON) {
      const pid = String(m.parentId)
      if (!buttonsByParent.has(pid)) buttonsByParent.set(pid, [])
      buttonsByParent.get(pid).push(m)
    }
  })
  buttonsByParent.forEach((list) => sortMenuNodes(list))
  return { byId, buttonsByParent }
}

/**
 * 與後端 MenuServiceImpl.menuTree 一致：根為 type=0 且 parentId=0，導航子節點不含按鈕。
 */
function buildNavSkeletonFromFlat (flat) {
  const navMenus = flat.filter((m) => m.type !== MENU_BUTTON)

  function buildChildren (parentId) {
    const children = navMenus.filter((m) => String(m.parentId) === String(parentId))
    sortMenuNodes(children)
    const built = []
    children.forEach((m) => {
      const menuList = buildChildren(m.id)
      if (m.type === MENU_CATALOG && menuList.length === 0) return
      built.push(Object.assign({}, m, { menuList }))
    })
    return built
  }

  const roots = navMenus.filter(
    (m) => m.type === MENU_CATALOG && (String(m.parentId) === '0' || Number(m.parentId) === 0)
  )
  sortMenuNodes(roots)
  return roots
    .map((r) => {
      const menuList = buildChildren(r.id)
      if (menuList.length === 0) return null
      return Object.assign({}, r, { menuList })
    })
    .filter(Boolean)
}

function resolvePageParent (node, parentMenu) {
  if (!node) return parentMenu
  if (Number(node.type) === MENU_BUTTON) return parentMenu
  if (Number(node.type) === 1) return node
  return parentMenu
}

function convertNavNodeToRoleTree (vm, node, parentMenu, buttonsByParent, flatById) {
  const pageParent = resolvePageParent(node, parentMenu)
  const navChildren = Array.isArray(node.menuList) ? node.menuList : []
  const childrens = []

  navChildren.forEach((child) => {
    childrens.push(convertNavNodeToRoleTree(vm, child, pageParent, buttonsByParent, flatById))
  })

  const buttons = buttonsByParent.get(String(node.id)) || []
  buttons.forEach((btn) => {
    const full = flatById.get(String(btn.id)) || btn
    childrens.push({
      ...full,
      id: String(full.id),
      type: MENU_BUTTON,
      childrens: [],
      displayLabel: rolePermissionNodeLabel(vm, full, pageParent)
    })
  })

  const fullNode = flatById.get(String(node.id)) || node
  const merged = {
    ...fullNode,
    id: String(fullNode.id),
    type: Number(fullNode.type),
    enName: fullNode.enName != null ? fullNode.enName : node.enName,
    name: fullNode.name != null ? fullNode.name : node.name,
    sort: fullNode.sort != null ? fullNode.sort : node.sort,
    childrens
  }
  merged.displayLabel = rolePermissionNodeLabel(vm, merged, parentMenu)
  return merged
}

/**
 * 角色授權樹 SSOT：僅依 coffee_menu 扁平資料重建導航骨架（與後端 menuTree 同 parentId／sort），
 * 按鈕權限（type=2）掛在對應頁面下；不讀側欄 session、不補孤兒節點。
 * @param {Vue} vm
 * @param {Array} flatList /coffee/menu/select-for-role 扁平資料
 */
export function buildRolePermissionTree (vm, flatList) {
  const flat = reparentLeoOpsOutOfSystemSettings(normalizeFlatMenus(flatList))
  const { byId: flatById, buttonsByParent } = indexFlatMenus(flat)
  const skeleton = splitDeviceChannelRoleSkeleton(buildNavSkeletonFromFlat(flat))
  return skeleton.map((node) =>
    convertNavNodeToRoleTree(vm, node, null, buttonsByParent, flatById)
  )
}

/** 導出供單測：DB 導航骨架（不含按鈕） */
export function buildMenuNavSkeletonFromFlat (flatList) {
  return buildNavSkeletonFromFlat(normalizeFlatMenus(flatList))
}

/** 收集授權樹 parentId 邊（不含按鈕），與侧栏 {@link collectNavMenuParentEdges} 比對格式 */
export function collectRoleTreeParentEdges (roots, parentId = '0', edges = []) {
  if (!Array.isArray(roots)) return edges
  roots.forEach((n) => {
    if (!n || n.id == null) return
    if (Number(n.type) === MENU_BUTTON) return
    const id = String(n.id)
    edges.push({ id, parentId: String(parentId) })
    if (Array.isArray(n.childrens) && n.childrens.length) {
      collectRoleTreeParentEdges(n.childrens, id, edges)
    }
  })
  return edges
}

/** 收集授權樹中所有導航節點 id（不含按鈕） */
export function collectRoleTreeNavIds (roots) {
  const ids = []
  function walk (nodes) {
    if (!Array.isArray(nodes)) return
    nodes.forEach((n) => {
      if (!n || n.id == null) return
      if (Number(n.type) !== MENU_BUTTON) ids.push(String(n.id))
      walk(n.childrens)
    })
  }
  walk(roots)
  return ids
}

/**
 * 角色編輯回顯：從 roleMenuList 提取可勾選 menuId（剔除 0／空／重複）。
 */
export function normalizeRoleMenuCheckIds (roleMenuList) {
  if (!Array.isArray(roleMenuList)) return []
  const seen = new Set()
  const out = []
  roleMenuList.forEach((item) => {
    if (!item) return
    const raw = item.menuId != null ? item.menuId : item.menu_id
    if (raw == null || raw === '') return
    if (String(raw) === '0' || Number(raw) === 0) return
    const id = String(raw)
    if (seen.has(id)) return
    seen.add(id)
    out.push(id)
  })
  return out
}




// WEBPACK FOOTER //
// ./src/utils/menuDisplay.js